import { tagged } from "@mirage/service-logging";
import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import { CacheKey } from "@mirage/service-typeahead-search/service/typeahead-cache";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import * as rx from "rxjs";

import {
  recommendationMatchesWithEmptyQuery,
  recommendationMatchesWithQuery,
} from "@mirage/service-typeahead-search/service/search/utils";
import { getSourceLimit } from "@mirage/service-typeahead-search/service/utils/source-helpers";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import * as sourceScore from "../scoring/source-score";
import { getResultSection } from "../utils/section-mapper";

const logger = tagged("typeahead/recommendations");

const FILTER_RESULT_LIMIT = 200;

export const raw: typeahead.TaggedSearch = (
  query,
  mode,
  { config, cache, grossLimit, sectionLimit },
) => {
  return rx.defer(() => {
    const omniboxResults = cache.all(
      CacheKey.OmniboxRecommendations,
    ) as Recommendation<"omnibox">[];

    const typeaheadResults = cache.all(
      CacheKey.Recommendations,
    ) as Recommendation[];

    // If omnibox is enabled but results are empty, use typeahead results
    // This is to ensure that for users transitioning to Omnibox they don't experience
    // a blank state
    // TODO (P1): When Omnibox is launched for Desktop/Launcher we will remove this
    // and the dcsGetTypeaheadRecommendations endpoint will be removed
    const isUsingOmniboxResults =
      config.isOmniboxEnabled && omniboxResults.length > 0;

    const gatedResults = isUsingOmniboxResults
      ? omniboxResults
      : typeaheadResults;

    let results = removeContacts(gatedResults);

    // Filter for summarizable results if the flag is enabled
    if (config.isSummarizableRecommendationsOnly) {
      results = onlySummarizable(results);
    }

    if (query === "") {
      logger.debug(
        `Getting matches with empty query, using omnibox: ${isUsingOmniboxResults}`,
      );
      results = recommendationMatchesWithEmptyQuery(
        results,
        FILTER_RESULT_LIMIT,
      );
    } else {
      results = recommendationMatchesWithQuery(
        query,
        results,
        FILTER_RESULT_LIMIT,
      );
    }

    return rx.from(
      tagAndInjectHits(
        results,
        query,
        mode,
        config,
        cache,
        grossLimit,
        sectionLimit,
      ),
    );
  });
};

/**
 * Helpers
 */

export function onlySummarizable(
  candidates: Recommendation[],
): Recommendation[] {
  return candidates.filter(
    (candidate) => candidate.summarizable === "yes_summarizable",
  );
}

export function removeContacts(candidates: Recommendation[]): Recommendation[] {
  return candidates.filter(
    (candidate) => candidate.recordType?.[".tag"] !== "contact",
  );
}

function tagAndInjectHits(
  results: Recommendation[],
  query: string,
  mode: typeahead.Mode,
  config: typeahead.Config,
  cache: TypeaheadCache,
  grossLimit: number,
  sectionLimit?: number,
): typeahead.ScoredResult[] {
  const sourceLimit = getSourceLimit(
    typeahead.ResultType.Recommendation,
    query,
    mode,
    config,
    grossLimit,
    sectionLimit,
  );

  const sectionId = getResultSection(
    typeahead.ResultType.Recommendation,
    mode,
    query,
    config,
  );

  return results
    .map((result) => {
      // we can have click tracking from the server through recommendations,
      // and we can have click tracking locally. The server can be delayed,
      // so in order to provide the best user experience, we use whichever
      // source has the highest non-title-match score for the recommendation
      // that way when the server catches up, it'll just start transparently
      // using the server-side score over the client-side score

      const resultWithLocalHits = primitives.recommendation(
        result.uuid,
        result,
        {
          hits: cache.getHits(result.uuid),
          sectionId,
        },
      );
      const resultWithRemoteHits = primitives.recommendation(
        result.uuid,
        result,
        {
          hits: {
            history: result.clicks,
            mostRecentMs: result.lastClickedMs,
          },
          sectionId,
        },
      );

      const localScoredResult = sourceScore.score(
        query,
        config,
      )(resultWithLocalHits);
      const remoteScoredResult = sourceScore.score(
        query,
        config,
      )(resultWithRemoteHits);

      return localScoredResult.score > remoteScoredResult.score
        ? localScoredResult
        : remoteScoredResult;
    })
    .sort((a, b) => b.score! - a.score!)
    .slice(0, sourceLimit);
}

export const search = wrappers.wrapped(SourceId.Recommendations, raw);
